import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUserPlus, FiEdit2, FiToggleLeft, FiToggleRight,
  FiSearch, FiX, FiChevronLeft, FiChevronRight,
  FiClock, FiShield, FiUser, FiCheck,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import API from '../utils/api';
import { GRANTABLE_PAGES, PAGE_GROUPS } from '../utils/staffPages';

// ─── tiny helpers ─────────────────────────────────────────────────────────────

const fmt = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const badge = (active) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 20,
  fontSize: '0.75rem',
  fontWeight: 600,
  background: active ? '#e6f9f0' : '#fef2f2',
  color:      active ? '#16a34a' : '#dc2626',
});

const PAGE_SIZE = 20;

// ─── Permission grid (shown inside modal) ────────────────────────────────────

/**
 * allowedPages state shape:
 *   { [key]: '' | 'view' | 'edit' }
 *   '' means unchecked / no access
 */
const PermissionGrid = ({ value, onChange }) => {
  const toggle = (key, mode) => {
    onChange(prev => {
      const current = prev[key] || '';
      let next;
      if (mode === 'edit') {
        // edit toggle: off → edit → off
        next = current === 'edit' ? '' : 'edit';
      } else {
        // view toggle: off → view → off (only if not already edit)
        if (current === 'edit') return prev; // edit implies view, ignore
        next = current === 'view' ? '' : 'view';
      }
      return { ...prev, [key]: next };
    });
  };

  const toggleGroup = (group) => {
    const groupKeys = GRANTABLE_PAGES.filter(p => p.group === group).map(p => p.key);
    const allEdit = groupKeys.every(k => value[k] === 'edit');
    onChange(prev => {
      const next = { ...prev };
      groupKeys.forEach(k => { next[k] = allEdit ? '' : 'edit'; });
      return next;
    });
  };

  return (
    <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10, padding: '4px 0' }}>
      {PAGE_GROUPS.map(group => {
        const pages = GRANTABLE_PAGES.filter(p => p.group === group);
        const allEdit = pages.every(p => value[p.key] === 'edit');
        return (
          <div key={group}>
            {/* Group header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 14px 4px',
              background: 'var(--bg-subtle, #f8fafc)',
              borderTop: '1px solid var(--border)',
              position: 'sticky', top: 0, zIndex: 1,
            }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {group}
              </span>
              <button
                type="button"
                onClick={() => toggleGroup(group)}
                style={{ fontSize: '0.72rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
              >
                {allEdit ? 'Remove all' : 'Grant all edit'}
              </button>
            </div>

            {/* Page rows */}
            {pages.map(p => {
              const current = value[p.key] || '';
              return (
                <div key={p.key} style={{
                  display: 'grid', gridTemplateColumns: '1fr 90px 90px',
                  alignItems: 'center', gap: 8,
                  padding: '7px 14px',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <span style={{ fontSize: '0.84rem', color: 'var(--text)' }}>{p.label}</span>

                  {/* View toggle */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    <input
                      type="checkbox"
                      checked={current === 'view' || current === 'edit'}
                      onChange={() => toggle(p.key, 'view')}
                      style={{ accentColor: 'var(--primary)', cursor: 'pointer' }}
                    />
                    View
                  </label>

                  {/* Edit toggle */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    <input
                      type="checkbox"
                      checked={current === 'edit'}
                      onChange={() => toggle(p.key, 'edit')}
                      style={{ accentColor: '#7c3aed', cursor: 'pointer' }}
                    />
                    Edit
                  </label>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

// Convert flat allowedPages array → grid value object
const pagesToGrid = (pages) => {
  const obj = {};
  // MySQL JSON columns can come back as strings — parse if needed
  const arr = typeof pages === 'string' ? JSON.parse(pages) : (pages || []);
  arr.forEach(p => {
    const [key, mode] = p.split(':');
    obj[key] = mode || 'edit'; // bare key = edit
  });
  return obj;
};

// Convert grid value object → flat allowedPages array
const gridToPages = (grid) => {
  return Object.entries(grid)
    .filter(([, v]) => v !== '')
    .map(([key, mode]) => (mode === 'edit' ? key : `${key}:${mode}`));
};

// ─── Staff Modal (Create / Edit) ─────────────────────────────────────────────

const StaffModal = ({ staff, onClose, onSaved }) => {
  const editing = !!staff;

  const [form, setForm] = useState({
    name:        staff?.name        || '',
    email:       staff?.email       || '',
    phone:       staff?.phone       || '',
    designation: staff?.designation || '',
    password:    '',
  });
  const [grid,    setGrid]    = useState(() => pagesToGrid(staff?.allowedPages));
  const [saving,  setSaving]  = useState(false);
  const [tab,     setTab]     = useState('info'); // 'info' | 'permissions'

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required.');
      return;
    }
    if (!editing && !form.password.trim()) {
      toast.error('Password is required for new staff.');
      return;
    }
    const pages = gridToPages(grid);
    if (pages.length === 0) {
      toast.error('Grant at least one page permission before saving.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name:        form.name.trim(),
        email:       form.email.trim(),
        phone:       form.phone.trim() || null,
        designation: form.designation.trim() || null,
        allowedPages: pages,
      };
      if (form.password.trim()) payload.password = form.password.trim();

      if (editing) {
        await API.put(`/admin/staff/${staff.id}`, payload);
        toast.success('Staff member updated.');
      } else {
        await API.post('/admin/staff', payload);
        toast.success('Staff member created.');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)',
    borderRadius: 8, fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
    background: 'var(--bg)', color: 'var(--text)',
  };
  const labelStyle = { fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: 'var(--bg)', borderRadius: 16, width: '100%', maxWidth: 560,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column',
        maxHeight: '90vh',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              {editing ? <FiEdit2 size={16} /> : <FiUserPlus size={16} />}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text)' }}>
                {editing ? 'Edit Staff Member' : 'Create Staff Member'}
              </div>
              {editing && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{staff.email}</div>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <FiX size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', margin: '16px 24px 0', gap: 4 }}>
          {[
            { id: 'info', label: 'Info', icon: <FiUser size={13} /> },
            { id: 'permissions', label: 'Permissions', icon: <FiShield size={13} /> },
          ].map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '8px 14px', border: 'none', cursor: 'pointer',
                borderBottom: tab === t.id ? '2px solid var(--primary)' : '2px solid transparent',
                color: tab === t.id ? 'var(--primary)' : 'var(--text-muted)',
                fontWeight: 600, fontSize: '0.84rem', background: 'none',
                marginBottom: -1,
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <form onSubmit={submit} style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {tab === 'info' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Full Name *</label>
                <input style={inputStyle} value={form.name} onChange={set('name')} placeholder="e.g. Priya Sharma" required />
              </div>
              <div>
                <label style={labelStyle}>Email Address *</label>
                <input style={inputStyle} type="email" value={form.email} onChange={set('email')} placeholder="priya@example.com" required disabled={editing} />
                {editing && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 3 }}>Email cannot be changed after creation.</div>}
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input style={inputStyle} value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" />
              </div>
              <div>
                <label style={labelStyle}>Designation / Job Title</label>
                <input style={inputStyle} value={form.designation} onChange={set('designation')} placeholder="e.g. Billing Manager, Production Staff" />
              </div>
              <div>
                <label style={labelStyle}>{editing ? 'New Password' : 'Password *'}</label>
                <input style={inputStyle} type="password" value={form.password} onChange={set('password')}
                  placeholder={editing ? 'Leave blank to keep current' : 'Set a secure password'}
                  required={!editing}
                />
              </div>

              {/* Permissions summary on info tab */}
              <div style={{ background: 'var(--bg-subtle, #f8fafc)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                  Permissions summary
                </div>
                {gridToPages(grid).length === 0
                  ? <div style={{ fontSize: '0.82rem', color: '#dc2626' }}>No permissions granted yet — go to the Permissions tab.</div>
                  : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {gridToPages(grid).map(p => {
                        const [key, mode] = p.split(':');
                        const page = GRANTABLE_PAGES.find(pg => pg.key === key);
                        return (
                          <span key={p} style={{
                            fontSize: '0.74rem', padding: '2px 8px', borderRadius: 20,
                            background: mode === 'view' ? '#eff6ff' : '#ede9fe',
                            color: mode === 'view' ? '#1d4ed8' : '#6d28d9',
                            fontWeight: 600,
                          }}>
                            {page?.label || key}{mode ? ` (${mode})` : ' (edit)'}
                          </span>
                        );
                      })}
                    </div>
                }
              </div>
            </div>
          )}

          {tab === 'permissions' && (
            <div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
                  Panel access
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  <strong>View</strong> — can see data but not change it. &nbsp;<strong>Edit</strong> — full read/write access. Edit implies View.
                </div>
              </div>
              <PermissionGrid value={grid} onChange={setGrid} />
            </div>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{
              padding: '9px 20px', border: '1.5px solid var(--border)', borderRadius: 8,
              background: 'var(--bg)', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem',
            }}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={{
              padding: '9px 20px', border: 'none', borderRadius: 8,
              background: 'var(--primary)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: '0.88rem', opacity: saving ? 0.7 : 1,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {saving ? 'Saving…' : editing ? (<><FiCheck size={14} /> Save changes</>) : (<><FiUserPlus size={14} /> Create staff</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Activity Log Drawer ─────────────────────────────────────────────────────

const ActivityDrawer = ({ staff, onClose }) => {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [pages,   setPages]   = useState(1);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await API.get(`/admin/staff/${staff.id}/activity?page=${p}&limit=30`);
      setLogs(data.logs || []);
      setPages(data.pages || 1);
      setPage(p);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [staff.id]);

  useEffect(() => { load(1); }, [load]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', justifyContent: 'flex-end', zIndex: 1000,
    }}>
      <div style={{
        width: '100%', maxWidth: 460, background: 'var(--bg)',
        height: '100%', display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 32px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: '1rem' }}>Activity Log</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{staff.name} · {staff.designation || 'Staff'}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <FiX size={20} />
          </button>
        </div>

        {/* Log list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 24px' }}>
          {loading && <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginTop: 24, textAlign: 'center' }}>Loading…</div>}
          {!loading && logs.length === 0 && (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginTop: 40, textAlign: 'center' }}>
              <FiClock size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
              <div>No activity recorded yet.</div>
            </div>
          )}
          {logs.map(log => (
            <div key={log.id} style={{
              display: 'flex', gap: 12, padding: '12px 0',
              borderBottom: '1px solid var(--border)',
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)',
                flexShrink: 0, marginTop: 6,
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.84rem', color: 'var(--text)', fontWeight: 500, lineHeight: 1.4 }}>
                  {log.description || log.action}
                </div>
                {log.entity && (
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    {log.entity}{log.entityId ? ` #${log.entityId}` : ''}
                  </div>
                )}
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>{fmt(log.createdAt)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: '14px 24px', borderTop: '1px solid var(--border)' }}>
            <button onClick={() => load(page - 1)} disabled={page === 1} style={{ background: 'none', border: 'none', cursor: page === 1 ? 'not-allowed' : 'pointer', color: 'var(--primary)' }}>
              <FiChevronLeft size={18} />
            </button>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', alignSelf: 'center' }}>Page {page} / {pages}</span>
            <button onClick={() => load(page + 1)} disabled={page === pages} style={{ background: 'none', border: 'none', cursor: page === pages ? 'not-allowed' : 'pointer', color: 'var(--primary)' }}>
              <FiChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main AdminStaff page ─────────────────────────────────────────────────────

const AdminStaff = () => {
  const [staff,      setStaff]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats,      setStats]      = useState({ total: 0, active: 0, inactive: 0 });

  const [showModal,   setShowModal]   = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);   // null = create mode
  const [logTarget,   setLogTarget]   = useState(null);   // staff object for activity drawer

  const load = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: targetPage, limit: PAGE_SIZE });
      if (search)       params.set('search',   search);
      if (statusFilter) params.set('isActive', statusFilter);
      const { data } = await API.get(`/admin/staff?${params}`);
      setStaff(data.staff || []);
      setTotalPages(data.pages || 1);
      setPage(targetPage);
      if (data.stats) setStats(data.stats);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { load(1); }, [statusFilter]); // eslint-disable-line

  const handleSearch = (e) => { e.preventDefault(); load(1); };

  const toggleStatus = async (member) => {
    if (!window.confirm(`${member.isActive ? 'Deactivate' : 'Activate'} ${member.name}?`)) return;
    try {
      await API.put(`/admin/staff/${member.id}`, { isActive: !member.isActive });
      toast.success(`${member.name} ${member.isActive ? 'deactivated' : 'activated'}.`);
      load(page);
    } catch {
      toast.error('Failed to update status.');
    }
  };

  const openCreate = () => { setEditTarget(null); setShowModal(true); };
  const openEdit   = (m) => { setEditTarget(m);   setShowModal(true); };
  const onSaved    = () => { setShowModal(false);  load(page); };

  // Styles
  const card = { background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px' };
  const th = { padding: '10px 14px', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const td = { padding: '13px 14px', fontSize: '0.84rem', color: 'var(--text)', verticalAlign: 'middle' };

  return (
    <div style={{ padding: '0 0 40px' }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text)', margin: 0 }}>Staff</h1>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>Manage admin panel access for your team.</div>
        </div>
        <button
          onClick={openCreate}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 9, fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' }}
        >
          <FiUserPlus size={16} /> Add staff member
        </button>
      </div>

      {/* ── Stats row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Staff',    value: stats.total,    color: 'var(--primary)' },
          { label: 'Active',         value: stats.active,   color: '#16a34a' },
          { label: 'Inactive',       value: stats.inactive, color: '#dc2626' },
        ].map(s => (
          <div key={s.label} style={{ ...card, display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '1.7rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{ ...card, marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', padding: '14px 18px' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 220 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, designation…"
              style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.86rem', boxSizing: 'border-box', background: 'var(--bg)', color: 'var(--text)', outline: 'none' }}
            />
          </div>
          <button type="submit" style={{ padding: '8px 14px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.84rem' }}>Search</button>
          {search && <button type="button" onClick={() => { setSearch(''); load(1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><FiX size={16} /></button>}
        </form>

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.86rem', background: 'var(--bg)', color: 'var(--text)', cursor: 'pointer' }}
        >
          <option value="">All statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {/* ── Table ── */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {loading
          ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
          : staff.length === 0
            ? (
              <div style={{ padding: 60, textAlign: 'center' }}>
                <FiUser size={36} style={{ color: 'var(--text-muted)', opacity: 0.3, marginBottom: 10 }} />
                <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No staff members found.</div>
                <button onClick={openCreate} style={{ marginTop: 14, padding: '8px 18px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                  Add first staff member
                </button>
              </div>
            )
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: 'var(--bg-subtle, #f8fafc)' }}>
                    <tr>
                      <th style={th}>Name</th>
                      <th style={th}>Designation</th>
                      <th style={th}>Pages granted</th>
                      <th style={th}>Last login</th>
                      <th style={th}>Status</th>
                      <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(m => (
                      <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: 'var(--text)' }}>{m.name}</div>
                          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{m.email}</div>
                          {m.phone && <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>{m.phone}</div>}
                        </td>
                        <td style={td}>{m.designation || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                        <td style={{ ...td, maxWidth: 220 }}>
                          {(() => {
                              const pages = typeof m.allowedPages === 'string'
                                ? JSON.parse(m.allowedPages)
                                : (m.allowedPages || []);
                              return (!pages || pages.length === 0)
                            ? <span style={{ color: '#dc2626', fontSize: '0.78rem' }}>No access</span>
                            : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {pages.slice(0, 4).map(p => {
                                  const [key, mode] = p.split(':');
                                  return (
                                    <span key={p} style={{
                                      fontSize: '0.7rem', padding: '1px 7px', borderRadius: 20,
                                      background: (!mode || mode === 'edit') ? '#ede9fe' : '#eff6ff',
                                      color:      (!mode || mode === 'edit') ? '#6d28d9'  : '#1d4ed8',
                                      fontWeight: 600,
                                    }}>
                                      {key}
                                    </span>
                                  );
                                })}
                                {pages.length > 4 && (
                                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>+{pages.length - 4} more</span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ ...td, fontSize: '0.78rem', color: 'var(--text-muted)' }}>{fmtDate(m.lastLoginAt)}</td>
                        <td style={td}><span style={badge(m.isActive)}>{m.isActive ? 'Active' : 'Inactive'}</span></td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                            <button
                              onClick={() => setLogTarget(m)}
                              title="View activity log"
                              style={{ padding: '5px 10px', border: '1.5px solid var(--border)', borderRadius: 7, background: 'var(--bg)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--text-muted)' }}
                            >
                              <FiClock size={13} /> Log
                            </button>
                            <button
                              onClick={() => openEdit(m)}
                              title="Edit"
                              style={{ padding: '5px 10px', border: '1.5px solid var(--border)', borderRadius: 7, background: 'var(--bg)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--text)' }}
                            >
                              <FiEdit2 size={13} /> Edit
                            </button>
                            <button
                              onClick={() => toggleStatus(m)}
                              title={m.isActive ? 'Deactivate' : 'Activate'}
                              style={{
                                padding: '5px 10px', border: 'none', borderRadius: 7, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', fontWeight: 600,
                                background: m.isActive ? '#fef2f2' : '#e6f9f0',
                                color:      m.isActive ? '#dc2626' : '#16a34a',
                              }}
                            >
                              {m.isActive ? <FiToggleRight size={14} /> : <FiToggleLeft size={14} />}
                              {m.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        }

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, padding: '14px 20px', borderTop: '1px solid var(--border)' }}>
            <button onClick={() => load(page - 1)} disabled={page === 1} style={{ background: 'none', border: 'none', cursor: page === 1 ? 'not-allowed' : 'pointer', color: 'var(--primary)', opacity: page === 1 ? 0.4 : 1 }}>
              <FiChevronLeft size={18} />
            </button>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Page {page} of {totalPages}</span>
            <button onClick={() => load(page + 1)} disabled={page === totalPages} style={{ background: 'none', border: 'none', cursor: page === totalPages ? 'not-allowed' : 'pointer', color: 'var(--primary)', opacity: page === totalPages ? 0.4 : 1 }}>
              <FiChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && (
        <StaffModal
          staff={editTarget}
          onClose={() => setShowModal(false)}
          onSaved={onSaved}
        />
      )}
      {logTarget && (
        <ActivityDrawer
          staff={logTarget}
          onClose={() => setLogTarget(null)}
        />
      )}
    </div>
  );
};

export default AdminStaff;